import { OutlineTextFieldWidget, TextInputType } from '@tableau/widgets';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { Messages } from '../../codegen/Localize';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { SiteRolePickerMenuHelper } from '../../ts/helpers/SiteRolePickerMenuHelper';
import { SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { ServerService } from '../../ts/services/ServerService';
import { AuthenticationProviderType } from '../../ts/services/SiteAuthenticationSettingsService';
import { AddLocalUserDialogType } from '../../userManagement/services/AddLocalUserAction';
import { IFormModel } from './AddLocalUsersDialog';
import { SiteRolePopover } from './SiteRolePopover';
import { ISiteRoleChanges, SiteRolesPickerContainer } from './SiteRolesPickerContainer';

export interface NewLocalUserFormState {
}

export interface NewLocalUserFormProps {
  readonly angularContext: AngularContext;
  readonly availableQuota: number;
  readonly busy: boolean;
  readonly formModel: IFormModel;
  readonly addLocalUserDialogType: string;
  readonly siteRole: string;
  readonly sitePickerMenuDisabled: boolean;
  readonly displayNameChangeHandler: (value: string) => void;
  readonly emailChangeHandler: (value: string) => void;
  readonly passwordChangeHandler: (value: string) => void;
  readonly confirmPasswordChangeHandler: (value: string) => void;
  readonly siteRoleChangesHandler: (siteRoleChanges: ISiteRoleChanges) => void;
  readonly getSiteRoleOptionBySiteRoleHandler: (siteRole: string) => SiteRoleOption;
  readonly onOptionSelectedHandler: (siteRoleOption: SiteRoleOption) => void;
}

const getDisabledRoleOptionsHandler = defaultMemoize((availableQuota: number, siteRoleOptions: SiteRoleOption[]): ReadonlyArray<SiteRoleOption> => SiteRolePickerMenuHelper.getPickerMenuDropdownOptionDisabledOptions(availableQuota, siteRoleOptions));

export class NewLocalUserForm extends React.Component<NewLocalUserFormProps, NewLocalUserFormState> {

  private server: ServerService = this.props.angularContext.getService('ServerService');
  private siteRolesOptionResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
  private translate = this.props.angularContext.getService('$translate');
  private roleOptions = this.server.isSessionServerAdmin() === false ? this.siteRolesOptionResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator) : this.siteRolesOptionResource.getAllSiteRoleOptions();

  public render() {
    return (
    <tbody className = "tb-newUserFlow-table">
      <tr>
        <td></td>
        <td className="tb-available-cell">
          <span className="tb-available-text"> {this.props.formModel.newUserFlowModel.usernameInfo} </span>
        </td>
      </tr>
      <tr>
        <td className="tb-text-input-label-cell">
          <label>{Messages.displayName_colon()} </label>
        </td>
        <td className="tb-settings-control tb-input-wide">
          <OutlineTextFieldWidget
                    handleChange={this.props.displayNameChangeHandler}
                    testId="display-name-input"
                    disabled={this.props.busy}
                    maxLength={100}
                    text={this.props.formModel.newUserFlowModel.displayName}/>
        </td>
      </tr>
      {this.showPasswordField()}
      {this.showConfirmPasswordField()}
      <tr>
        <td className="tb-text-input-label-cell">
          <label>{Messages.emailOptional_colon()}</label>
        </td>
        <td className="tb-settings-control tb-input-wide">
          <OutlineTextFieldWidget
                    handleChange={this.props.emailChangeHandler}
                    testId="add-local-user-email"
                    disabled={this.props.busy}
                    maxLength={100}
                    text={this.props.formModel.newUserFlowModel.email}/>
        </td>
      </tr>
      {this.showSitePickerMenu()}
      {this.showSiteRolesPicker()}
    </tbody>);
  }

  private showPasswordField(): JSX.Element {
    const authenticationProvider = this.props.formModel.authenticationProviderSelectorModel.authenticationProvider;
    let shouldShowPasswordField = authenticationProvider.type === AuthenticationProviderType.default && !this.server.isAuthenticationSaml();
    if (shouldShowPasswordField) {
      return (<tr>
                <td className="tb-text-input-label-cell">
                  <label>{Messages.password_colon()}</label>
                </td>
                <td className="tb-settings-control tb-input-wide">
                  <OutlineTextFieldWidget
                    type={TextInputType.Password}
                    handleChange={this.props.passwordChangeHandler}
                    testId="add-local-user-password"
                    disabled={this.props.busy}
                    maxLength={100}
                    text={this.props.formModel.newUserFlowModel.password}/>
                </td>
              </tr>);
    }
    return (null);
  }

  private showConfirmPasswordField(): JSX.Element {
    const authenticationProvider = this.props.formModel.authenticationProviderSelectorModel.authenticationProvider;
    let shouldShowPasswordField = authenticationProvider.type === AuthenticationProviderType.default && !this.server.isAuthenticationSaml();
    if (shouldShowPasswordField) {
      return(<tr>
              <td className="tb-text-input-label-cell">
                <label>{Messages.confirmPassword_colon()}</label>
              </td>
              <td className="tb-settings-control tb-input-wide">
                <OutlineTextFieldWidget
                  type={TextInputType.Password}
                  handleChange={this.props.confirmPasswordChangeHandler}
                  testId="add-local-user-confirm-password"
                  disabled={this.props.busy}
                  maxLength={100}
                  text={this.props.formModel.newUserFlowModel.confirmPassword}/>
              </td>
            </tr>);
    }
    return (null);
  }

  private showSiteRolesPicker(): JSX.Element {
    if (this.props.addLocalUserDialogType === AddLocalUserDialogType.SERVER) {
      return(<tr>
              <td colSpan={2}>
                <div className="tb-picker-container tb-picker-border">
                  <SiteRolesPickerContainer
                    changes={this.props.siteRoleChangesHandler}
                    busy={this.props.busy}
                    angularContext={this.props.angularContext}
                  />
                </div>
              </td>
            </tr>);
    }
    return (null);
  }

  private showSitePickerMenu(): JSX.Element {
    if (this.props.addLocalUserDialogType === AddLocalUserDialogType.SITE) {
      return(<tr>
              <td className="tb-react-text-input-label-cell">{Messages.siteRole_colon()}</td>
              <td className="tb-settings-control tb-react-input-wide">
                <SiteRolePickerMenu
                  translate={this.translate.instant}
                  siteRoleOptions={this.roleOptions}
                  getSiteRoleOptionBySiteRole={this.props.getSiteRoleOptionBySiteRoleHandler}
                  onOptionSelected={this.props.onOptionSelectedHandler}
                  selectedRole={this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(this.props.siteRole)}
                  isDisabled={this.props.busy || this.props.sitePickerMenuDisabled}
                  disabledRoleOptions={[...getDisabledRoleOptionsHandler(this.props.availableQuota, this.roleOptions)]}/>
                <SiteRolePopover angularContext={this.props.angularContext} />
              </td>
            </tr>);
    }
    return (null);
  }

}
